import React, { useEffect } from 'react';
import {
  Box,
  Stack,
  Button,
  Divider,
  Grid,
  Typography,
  TextField,
} from '@mui/material';
import Iconify from '../../../components/Iconify';
import { useRef } from 'react';
import { FieldArray } from 'formik';
import AutoComplete from '../../../components/AutoComplete';
import { NumericFormatCustom } from '../../../components/NumericCustomFormatter';
import { useAppDispatch, useAppSelector } from '../../../store';
import {
  findSelectPropertyFeature,
  searchSelectPropertyFeature,
} from '../../../store/selectPropertyFeature/api';
import { PropertyFeature } from '../../../store/propertyFeature/api/interface';
import { uniqBy } from 'lodash';
import {
  setSkip as setSkipPropertyFeature,
  reset as resetPropertyFeature,
} from '../../../store/selectPropertyFeature/selectPropertyFeatureSlice';

interface IProps {
  isAddMode: boolean;
  response: any;
  values: any;
  errors: any;
  touched: any;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => Promise<any>;
  handleChange: any;
}

const DetailsForm: React.FC<IProps> = ({
  values,
  handleChange,
  setFieldValue,
  errors,
  touched,
}) => {
  const dispatch = useAppDispatch();
  const arrayHelpersRef = useRef<any>(null);

  // Property Feature
  const {
    data: propertyFeatureData,
    isLoading: isPropertyFeatureLoading,
    skip: skipPropertyFeature,
    hasMore: hasMorePropertyFeature,
    status: propertyFeatureStatus,
  } = useAppSelector((state) => state.selectPropertyFeature);

  useEffect(() => {
    if (values?.categoryId && propertyFeatureStatus === 'idle') {
      dispatch(
        findSelectPropertyFeature({
          limit: 10,
          skip: skipPropertyFeature,
          propertyCategoryId: values?.categoryId,
          ...(values?.subCategoryId && {
            propertySubCategoryId: values?.subCategoryId,
          }),
          activeOnly: true,
        })
      );
    }
  }, [
    skipPropertyFeature,
    values?.categoryId,
    values?.subCategoryId,
    propertyFeatureStatus,
  ]);

  const onScrollFetchPropertyFeature = (
    event: React.UIEvent<HTMLElement, UIEvent>
  ) => {
    if (
      event.currentTarget.scrollTop + 300 >= event.currentTarget.scrollHeight &&
      hasMorePropertyFeature
    ) {
      dispatch(setSkipPropertyFeature(skipPropertyFeature + 10));
      dispatch(
        findSelectPropertyFeature({
          limit: 10,
          skip: skipPropertyFeature + 10,
          propertyCategoryId: values?.categoryId,
          ...(values?.subCategoryId && {
            propertySubCategoryId: values?.subCategoryId,
          }),
          activeOnly: true,
        })
      );
    }
  };

  const searchPropertyFeature = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!e.target.value) {
      dispatch(resetPropertyFeature());
      return;
    }
    const params = {
      activeOnly: true,
      propertyCategoryId: values?.categoryId,
      ...(values?.subCategoryId && {
        propertySubCategoryId: values?.subCategoryId,
      }),
      ...(e.target.value && { limit: 50 }),
      ...(e.target.value && { skip: 0 }),
      ...(e.target.value && { search: e.target.value }),
    };
    dispatch(setSkipPropertyFeature({ skip: 0 }));
    dispatch(searchSelectPropertyFeature(params));
  };

  const uniquePropertyFeatureData = uniqBy([...propertyFeatureData], 'id');

  return (
    <Box sx={{ py: 3 }}>
      <Typography
        variant='h6'
        sx={{ color: 'text.disabled', mb: 2 }}
      >
        Property Features:
      </Typography>
      <Stack
        divider={
          <Divider
            flexItem
            sx={{ borderStyle: 'dashed' }}
          />
        }
        spacing={3}
      >
        <FieldArray
          name='propertyFeatureItems'
          render={(arrayHelpers: any) => {
            arrayHelpersRef.current = arrayHelpers;
            return (
              <>
                {values?.propertyFeatureItems?.map((item: any, idx: any) => {
                  return (
                    <React.Fragment key={item?.featureId + idx}>
                      <Stack
                        alignItems='flex-end'
                        spacing={1.5}
                      >
                        <Grid
                          container
                          spacing={3}
                        >
                          <Grid
                            item
                            xs={12}
                            sm={4}
                          >
                            <AutoComplete<PropertyFeature>
                              key={`feature-${idx}-${values.propertyFeatureItems?.[idx]?.featureId || 'empty'}`}
                              onChange={(event, newValue) => {
                                if (typeof newValue === 'string') {
                                } else if (newValue) {
                                  setFieldValue(
                                    `propertyFeatureItems[${idx}].featureId`,
                                    newValue.id
                                  );
                                } else {
                                  setFieldValue(
                                    `propertyFeatureItems[${idx}].featureId`,
                                    ''
                                  );
                                }
                              }}
                              onInputChange={(event, newInputValue) => {
                                if (!newInputValue) {
                                  setFieldValue(
                                    `propertyFeatureItems[${idx}].featureId`,
                                    ''
                                  );
                                }
                              }}
                              renderOption={(props, option) => (
                                <li {...props}>{option.labelEn}</li>
                              )}
                              getOptionLabel={(option) => option.labelEn}
                              error={Boolean(
                                touched?.propertyFeatureItems?.[idx]
                                  ?.featureId &&
                                  errors?.propertyFeatureItems?.[idx]?.featureId
                              )}
                              helperText={
                                Boolean(
                                  touched?.propertyFeatureItems?.[idx]
                                    ?.featureId &&
                                    errors?.propertyFeatureItems?.[idx]
                                      ?.featureId
                                )
                                  ? errors.propertyFeatureItems?.[idx]
                                      ?.featureId ?? ''
                                  : ''
                              }
                              fetchFunction={onScrollFetchPropertyFeature}
                              label={'Select Property Feature'}
                              loading={isPropertyFeatureLoading}
                              options={uniquePropertyFeatureData || []}
                              searchFunction={searchPropertyFeature}
                              value={
                                values.propertyFeatureItems?.[idx]?.featureId && uniquePropertyFeatureData?.length > 0
                                  ? (uniquePropertyFeatureData.find(
                                      (propertyFeature) =>
                                        propertyFeature.id ===
                                        values.propertyFeatureItems?.[idx]
                                          ?.featureId
                                    ) || null)
                                  : null
                              }
                            />
                          </Grid>

                          <Grid
                            item
                            xs={12}
                            sm={4}
                          >
                            <TextField
                              id='value'
                              name={`propertyFeatureItems[${idx}].value`}
                              value={
                                values.propertyFeatureItems[idx].value ?? ''
                              }
                              onChange={handleChange}
                              label={'Value'}
                              fullWidth
                              variant='outlined'
                              InputProps={{
                                inputComponent: NumericFormatCustom as any,
                              }}
                              error={Boolean(
                                touched?.propertyFeatureItems?.[idx]?.value &&
                                  errors?.propertyFeatureItems?.[idx]?.value
                              )}
                              helperText={
                                Boolean(
                                  touched?.propertyFeatureItems?.[idx]?.value &&
                                    errors?.propertyFeatureItems?.[idx]?.value
                                ) && errors.propertyFeatureItems?.[idx]?.value
                              }
                            />
                          </Grid>
                        </Grid>
                        {
                          <div>
                            {idx + 1 ===
                              values?.propertyFeatureItems?.length && (
                              <Button
                                size='small'
                                startIcon={<Iconify icon='eva:plus-fill' />}
                                onClick={() =>
                                  arrayHelpersRef.current.push({
                                    featureId: '',
                                    value: '',
                                  })
                                }
                                sx={{
                                  flexShrink: 0,
                                  paddingRight: '15px',
                                }}
                              >
                                {'Add Property Feature'}
                              </Button>
                            )}
                            <Button
                              size='small'
                              color='error'
                              startIcon={<Iconify icon='eva:trash-2-outline' />}
                              onClick={() => {
                                arrayHelpers?.remove(idx);
                              }}
                              disabled={
                                values?.propertyFeatureItems?.length === 1
                              }
                            >
                              {'Remove'}
                            </Button>
                          </div>
                        }
                      </Stack>
                      <Divider
                        sx={{
                          my: 3,
                          borderStyle: 'dashed',
                        }}
                      />
                    </React.Fragment>
                  );
                })}
              </>
            );
          }}
        ></FieldArray>
      </Stack>
    </Box>
  );
};
export default DetailsForm;
